import { groupBy, has, indexBy, isEmpty, isNil, propOr, uniq } from '../../utils/fp';
import { getDataStructureAnnotations, getDataflowAnnotations } from './accessors/structure';

const parseAutomatedSelections = annotations => {
  const titles = annotations
    .filter(annotation => annotation.type === 'ALWAYS_DISPLAY_PARENTS')
    .map(annotation => annotation.title);
  const entries = titles.join(',').split(',');
  return entries.reduce((acc, entry) => {
    if (isEmpty(entry)) {
      return acc;
    }
    const [dimension, _levels] = entry.split('=');
    if (isNil(_levels)) {
      return { ...acc, [dimension]: [] };
    }
    const levels = _levels.split('+').reduce((_acc, str) => {
      const match = str.match(/LEVEL([\d]+)/) || [];
      if (isEmpty(match)) {
        return _acc;
      }
      const index = Number(match[1]) - 1;
      if (isNaN(index)) {
        return _acc;
      }
      return [..._acc, index];
    }, []);
    if (has(dimension, acc)) {
      if (isEmpty(acc[dimension])) {
        return acc;
      }
      return { ...acc, [dimension]: uniq([...levels, ...acc[dimension]]) };
    }
    return { ...acc, [dimension]: levels };
  }, {});
};

export const getAutomatedSelections = structure => {
  return parseAutomatedSelections([
    ...getDataStructureAnnotations(structure),
    ...getDataflowAnnotations(structure),
  ]);
};

export const applyAutomatedSelection = (values, levels) => {
  const grouped = groupBy(value => propOr('#ROOT', 'parentId', value), values);

  const getValues = parentsIds => {
    const level = parentsIds.length;
    const parentId = level === 0 ? '#ROOT' : parentsIds.at(-1);
    return propOr([], parentId, grouped).flatMap(val => {
      const id = val.hierarchicalId || val.id;
      const isParent = has(id, grouped);
      return [
        {
          ...val,
          isForced: (isEmpty(levels) || levels.includes(level)) && isParent,
          imposedIds: isEmpty(levels)
            ? parentsIds
            : levels.map(lvl => parentsIds[lvl]).filter(Boolean),
        },
        ...(isParent ? getValues([...parentsIds, id]) : []),
      ];
    });
  };

  return getValues([]);
};

const getAdvancedValuesPathes = hierarchy => {
  const paired = Object.entries(hierarchy);

  return paired.reduce((acc, [parentsKey, values]) => {
    const path = parentsKey === '#ROOT' ? [] : parentsKey.split('.');
    let res = acc;
    values.forEach(value => {
      if (has(value, res)) {
        res = { ...res, [value]: [...res[value], path] };
      } else {
        res = { ...res, [value]: [path] };
      }
    });
    return res;
  }, {});
};

const getSimpleValuesPathes = values => {
  const indexedValues = indexBy(value => value.id, values);

  const getParents = value => {
    if (isNil(value.parent)) {
      return [];
    }
    if (isNil(indexedValues[value.parent])) {
      return [];
    }
    const parent = indexedValues[value.parent];
    return [...getParents(parent), value.parent];
  };

  return values.reduce((acc, value) => ({ ...acc, [value.id]: [getParents(value)] }), {});
};

export const computeLevelSelections = (selection, dimension, hierarchy) => {
  const indexedPaths = isNil(hierarchy)
    ? getSimpleValuesPathes(dimension.values)
    : getAdvancedValuesPathes(hierarchy);

  const leveledValues = Object.entries(indexedPaths).reduce(
    (acc, [id, paths]) =>
      paths.reduce((_acc, path) => {
        const level = `LEVEL${path.length + 1}`;
        return { ..._acc, [level]: [...(_acc[level] || []), id] };
      }, acc),
    {},
  );

  return uniq(selection.flatMap(id => propOr(id, id, leveledValues)));
};

export const parseDataquery = (dataquery, dimensions) => {
  const split = dataquery.split('.');

  return dimensions.reduce((acc, dim, index) => {
    const selection = split[index];
    if (isNil(selection) || isEmpty(selection)) {
      return acc;
    }
    return { ...acc, [dim.id]: selection.split('+').sort() };
  }, {});
};

export const selectAutomatedSelections = (
  selection,
  dimensions,
  automatedSelections,
  hierarchies,
) =>
  dimensions.reduce((acc, dim) => {
    if (!has(dim.id, automatedSelections) || isEmpty(propOr([], dim.id, acc))) {
      return acc;
    }
    const levels = automatedSelections[dim.id];
    const pathes = has(dim.id, hierarchies)
      ? getAdvancedValuesPathes(hierarchies[dim.id])
      : getSimpleValuesPathes(propOr([], 'values', dim));

    const newSelection = acc[dim.id]
      .map(value => {
        const valuePathes = propOr([], value, pathes);

        const refinedPathes = isEmpty(levels)
          ? valuePathes
          : valuePathes.map(path => levels.map(lvl => path[lvl]).filter(Boolean));

        return [...refinedPathes, value];
      })
      .flat(Infinity);
    return { ...acc, [dim.id]: uniq(newSelection) };
  }, selection);
